// lib/axios.ts
import axios from "axios";
import { toast } from "sonner";
import { loginUrlWithCallback } from "@/utils/callbackUrl";

// Helper function to get token from cookie (client-side only)
const getTokenFromCookie = (): string | null => {
  if (typeof document === "undefined") return null;

  const token = document.cookie
    .split("; ")
    .find(row => row.startsWith("token="))
    ?.split("=")[1];

  return token || null;
};

const apiInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_APP_URL || "http://localhost:3000",
  withCredentials: true, // send cookies for auth
});

// A bare instance without interceptors to call refresh endpoint
const bareAxios = axios.create({
  baseURL: process.env.NEXT_PUBLIC_APP_URL || "http://localhost:3000",
  withCredentials: true,
});

let refreshPromise: Promise<void> | null = null;

// Ensure only one refresh request runs at a time
async function refreshAccessTokenOnce() {
  if (!refreshPromise) {
    refreshPromise = bareAxios
      .post("/api/auth/refresh")
      .then(() => undefined)
      .catch(err => {
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Add a response interceptor to handle 429 errors globally
// Augment request config to track a one-time retry
type AugmentedAxiosRequestConfig = typeof apiInstance.defaults & { _retry?: boolean } & any;

// Add response interceptor to handle 401 and auto-refresh
apiInstance.interceptors.response.use(
  response => response,
  async error => {
    const { response, config } = error || {};
    const originalRequest = (config as AugmentedAxiosRequestConfig) || {};

    // Global 429 handling
    if (response && response.status === 429) {
      const retryAfterHeader = response.headers["retry-after"];
      const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 60;
      toast.error("Too many requests", {
        description: `You're doing that too fast. Please wait about ${retryAfterSeconds} seconds before trying again.`,
        duration: 5000,
      });
    }

    // Auto-refresh on 401 for non-refresh/login calls
    if (
      response &&
      response.status === 401 &&
      !originalRequest._retry &&
      typeof originalRequest.url === "string" &&
      !originalRequest.url.includes("/api/auth/refresh") &&
      !originalRequest.url.includes("/api/auth/login") &&
      !originalRequest.url.includes("/api/auth/register") &&
      !originalRequest.url.includes("/api/auth/mfa/verify") &&
      !originalRequest.url.includes("/api/auth/mfa/resend") &&
      !originalRequest.url.includes("/api/auth/reset-link")
    ) {
      try {
        originalRequest._retry = true; // Mark request as retried
        await refreshAccessTokenOnce();
        // After successful refresh, retry the original request
        return apiInstance.request(originalRequest);
      } catch {
        // If refresh fails: best-effort logout (clear cookies) then redirect to login (client-side only)
        try {
          await bareAxios.get("/api/auth/logout");
        } catch {
          // Silent fail on logout error
        }
        if (typeof window !== "undefined") {
          toast.error("Session expired. Please log in again.");
          const loginUrl = process.env.NEXT_PUBLIC_LOGIN_URL || "/login";
          // The session died under them mid-navigation; bring them back to the page they were on.
          window.location.href = loginUrlWithCallback(
            loginUrl,
            `${window.location.pathname}${window.location.search}`,
          );
        }
        return Promise.reject(error);
      }
    }
    // Do not redirect on 403 — that means "authenticated but forbidden".
    // Redirecting to /login while still logged in bounces to /home (Settings, etc.).

    return Promise.reject(error);
  },
);

export const backendApiInstance = axios.create({
  baseURL: process.env.SERVER_URL || "http://localhost:3000",
  headers: {
    Authorization: `Bearer ${process.env.INTERNAL_API_SECRET}`, // HACK: It was introduced to solve 401 unauthenticated errors on internal apicalls for private apis
  },
  withCredentials: true,
});

export const chatServerApiInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_CHAT_SERVER_URL || "http://localhost:8000",
  headers: {
    Authorization: `Bearer ${process.env.INTERNAL_API_SECRET}`, // HACK: It was introduced to solve 401 unauthenticated errors on internal apicalls for private apis
  },
});

// Add request interceptor to chatServerApiInstance for authentication
chatServerApiInstance.interceptors.request.use(
  config => {
    // Get token from cookie
    const token = getTokenFromCookie();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

export default apiInstance;
